import { env } from '../../../env'
import axios from 'axios'
import { MerchantsListResponse } from './_rest_models'
import { IsGranted } from '../../modules/auth/PrivateRoute'
import { ROLE, UserModel } from '../../modules/auth'
import { ApiResponse } from '../api/response.types'
import { ICashBoxesListRes, IPaymentAdapterListRes, CashBoxListParams, CreateCashBoxRequest, CreateCashBoxResponse, CashBoxDetailsResponse, UpdateCashBoxStatusRequest, UpdateCashBoxStatusResponse, CashBoxApiAccessResponse, ICashBoxBalancePolicyRes } from '../../../types'

const API_URL = env.REACT_APP_API_URL

export const GET_LIST_CASH_BOXES = `${API_URL}/api/internal/cash-box/list`
export const GET_CASH_BOX_LOOKUP = `${API_URL}/api/internal/cash-box/lookup`
export const GET_CASH_BOX_DETAILS = `${API_URL}/api/internal/cash-box`
export const CREATE_CASH_BOX = `${API_URL}/api/internal/cash-box/create`
export const UPDATE_CASH_BOX_STATUS = `${API_URL}/api/internal/cash-box`
export const GET_LIST_PAYMENT_ADAPTER = `${API_URL}/api/internal/payment-adapter/list`
export const GET_EMPLOYEE_LIST_MERCHANTS = `${API_URL}/api/internal/merchant/list`
export const GET_LIST_MERCHANTS = `${API_URL}/api/merchant/list`

export function getListCashBoxesForSelect() {
  return axios.get<Partial<ICashBoxesListRes>>(GET_LIST_CASH_BOXES, {
    params: {
      perPage: 99,
      page: 1,
      enabled: true,
    },
  })
}

export function getCashBoxesLookup() {
  return axios.get<ApiResponse<Array<{ value: number; label: string }>>>(GET_CASH_BOX_LOOKUP)
}

/**
 * Мерчанты для выпадашки со значением **внутреннего id** (в отличие от
 * `getMerchantLookupWithToken`, где value — токен). Нужен там, где id уходит в
 * запрос как число: фильтры дашборда мэтчинга, drilldown. Роли — ROLE_MANAGER /
 * ROLE_MERCHANT_MANAGER.
 */
export function getMerchantLookup() {
  return axios.get<ApiResponse<Array<{ value: number; label: string }>>>(
    `${API_URL}/api/merchant/lookup`
  )
}

/** Платёжные методы для выпадашки: value = id метода, label = его тип. */
export function getPaymentMethodLookup() {
  return axios.get<ApiResponse<Array<{ value: number; label: string }>>>(
    `${API_URL}/api/payment-method/lookup`
  )
}

export function getMerchantLookupWithToken() {
  return axios.get<ApiResponse<Array<{ value: string; label: string }>>>(
    `${API_URL}/api/merchant/lookup-with-token`
  )
}

export function getPaymentAdapterLookup(params?: { merchant?: string; enabledOnly?: boolean }) {
  return axios.get<ApiResponse<Array<{ value: number; label: string }>>>(
    `${API_URL}/api/internal/payment-adapter/lookup`,
    { params }
  )
}

export function getListCashBoxes(params?: CashBoxListParams) {
  return axios.get<ICashBoxesListRes>(GET_LIST_CASH_BOXES, {
    params: {
      perPage: params?.perPage || 20,
      page: params?.page || 1,
      ...(params?.enabled !== undefined && { enabled: params.enabled }),
    },
  })
}

export function createCashBox(data: CreateCashBoxRequest) {
  return axios.post<CreateCashBoxResponse>(CREATE_CASH_BOX, data)
}

export function getCashBoxDetails(id: number) {
  return axios.get<CashBoxDetailsResponse>(`${GET_CASH_BOX_DETAILS}/${id}`)
}

export function updateCashBoxStatus(id: number, data: UpdateCashBoxStatusRequest) {
  return axios.post<UpdateCashBoxStatusResponse>(`${UPDATE_CASH_BOX_STATUS}/${id}/status`, data)
}

// CPAYMENT-1546: operational block / unblock (overdraft limit moved to the balance policy below)
export function blockCashBox(id: number, reason: string) {
  return axios.post<CashBoxDetailsResponse>(`${GET_CASH_BOX_DETAILS}/${id}/block`, { reason })
}

export function unblockCashBox(id: number) {
  return axios.post<CashBoxDetailsResponse>(`${GET_CASH_BOX_DETAILS}/${id}/unblock`)
}

// CPAYMENT-1572: учёт баланса кассы — тумблер и допустимый минус одним запросом
export function getCashBoxBalancePolicy(id: number) {
  return axios.get<ICashBoxBalancePolicyRes>(`${GET_CASH_BOX_DETAILS}/${id}/balance-policy`)
}

export function setCashBoxBalancePolicy(
  id: number,
  body: { enforceBalance: boolean; overdraftLimit: string }
) {
  return axios.post<ICashBoxBalancePolicyRes>(`${GET_CASH_BOX_DETAILS}/${id}/balance-policy`, body)
}

// CPAYMENT-1693 (временное решение): программный доступ кассы — включение и перевыпуск ключа
export function setCashBoxApiAccess(id: number, enabled: boolean) {
  return axios.post<CashBoxApiAccessResponse>(`${GET_CASH_BOX_DETAILS}/${id}/api-access`, { enabled })
}

export function rotateCashBoxApiToken(id: number) {
  return axios.post<CashBoxApiAccessResponse>(`${GET_CASH_BOX_DETAILS}/${id}/api-access/rotate`)
}

// AC4: привязка/отвязка менеджера мерчантов к кассе (карточка кассы; ADMIN/MANAGER)
export function attachCashBoxManager(cashBoxId: number, managerId: number) {
  return axios.post<CashBoxDetailsResponse>(`${GET_CASH_BOX_DETAILS}/${cashBoxId}/managers/${managerId}`)
}

export function detachCashBoxManager(cashBoxId: number, managerId: number) {
  return axios.delete<CashBoxDetailsResponse>(`${GET_CASH_BOX_DETAILS}/${cashBoxId}/managers/${managerId}`)
}

// Список пользователей роли «Менеджер мерчантов» (для выбора при привязке к кассе)
export function getMerchantManagersForSelect() {
  return axios.get<{ employees: Array<{ id: number; uuid: string }>; success: boolean }>(
    `${API_URL}/api/internal/employee/list`,
    { params: { role: 'ROLE_MERCHANT_MANAGER', enabled: true, perPage: 99, page: 1 } }
  )
}

export function getListPaymentAdapterForSelect() {
  return axios.get<Partial<IPaymentAdapterListRes>>(GET_LIST_PAYMENT_ADAPTER, {
    params: {
      perPage: 99,
      page: 1,
      enabled: true,
    },
  })
}

export function getListMerchantsForSelect(currentUser?: UserModel) {
  // ROLE_MERCHANT_MANAGER не имеет ROLE_EMPLOYEE, но должен брать СВОЙ скоупленный список мерчантов
  // из internal-эндпоинта (merchants_managers), а не публичный /api/merchant/list
  const url = IsGranted([ROLE.ROLE_EMPLOYEE, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)
    ? GET_EMPLOYEE_LIST_MERCHANTS
    : GET_LIST_MERCHANTS

  return axios.get<Partial<MerchantsListResponse>>(url, {
    params: {
      perPage: 99,
      page: 1,
      enabled: true,
    },
  })
}

export function downloadFile(url: string) {
  return axios.get(url, { responseType: 'blob' })
}
